import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { siteService, SiteMetadata } from '../services/siteService';
import { LOADING_STATE } from '../types/constants';

interface SiteState {
  siteCodes: string[];
  siteCodesStatus: LOADING_STATE;
  currentSite: SiteMetadata | null;
  currentSiteStatus: LOADING_STATE;
}

const initialState: SiteState = {
  siteCodes: [],
  siteCodesStatus: 'idle',
  currentSite: null,
  currentSiteStatus: 'idle',
};

export const fetchSiteCodes = createAsyncThunk(
  'site/fetchSiteCodes',
  async (_, { rejectWithValue }) => {
    try {
      return await siteService.fetchSiteCodes();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch site codes');
    }
  }
);

export const fetchSiteMetadata = createAsyncThunk(
  'site/fetchSiteMetadata',
  async (siteCode: string, { rejectWithValue }) => {
    try {
      return await siteService.fetchSiteMetadata(siteCode);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch site metadata');
    }
  }
);

const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    clearCurrentSite(state) {
      state.currentSite = null;
      state.currentSiteStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteCodes.pending, (state) => {
        state.siteCodesStatus = 'pending';
      })
      .addCase(fetchSiteCodes.fulfilled, (state, action: PayloadAction<string[]>) => {
        state.siteCodesStatus = 'fulfilled';
        state.siteCodes = action.payload;
      })
      .addCase(fetchSiteCodes.rejected, (state) => {
        state.siteCodesStatus = 'rejected';
      })
      .addCase(fetchSiteMetadata.pending, (state) => {
        state.currentSiteStatus = 'pending';
      })
      .addCase(fetchSiteMetadata.fulfilled, (state, action: PayloadAction<SiteMetadata>) => {
        state.currentSiteStatus = 'fulfilled';
        state.currentSite = action.payload;
      })
      .addCase(fetchSiteMetadata.rejected, (state) => {
        state.currentSiteStatus = 'rejected';
      });
  },
});

export const { clearCurrentSite } = siteSlice.actions;
export default siteSlice.reducer;
